/** @jsxImportSource @emotion/react */
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { StyledText } from "../assets/typography/typography";
import { colors } from "./colors";
import { css } from "@emotion/react";
import { Popover } from "antd";

interface NavigationLinkProps {
  to: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  exact?: boolean;
  isTopBarLink?: boolean;
  locked?: boolean;
  width?: string;
  onClick?: () => void;
}

const topbarTitleContainerStyle = css`
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: 0px;
  gap: 8px;
  height: fit-content;
`;

const iconWrapperStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
`;

const arrowWrapperStyle = css`
  ${iconWrapperStyle}
  color: ${colors.textPrimary};
`;

export const NavigationLink = ({
  to,
  children,
  icon,
  exact,
  isTopBarLink,
  locked = false,
  width,
  onClick,
}: NavigationLinkProps) => {
  const navLinkStyleBase = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    margin-bottom: 0px;
    text-decoration: none !important;
    border-radius: 4px;
    gap: 8px;
  `;

  const navLinkStyle = css`
    ${navLinkStyleBase}
    background-color: ${colors.transparent};
    color: ${colors.textPrimary};
    &:hover {
      color: ${colors.textPrimary};
      background-color: ${colors.ctaLight};
    }
  `;

  const lockedNavLinkStyle = css`
    ${navLinkStyleBase}
    background-color: ${colors.white};
    color: ${colors.textSecondary};
    border: none;
    width: ${width || "fit-content"};
  `;

  const [showArrow, setShowArrow] = useState<boolean>(false);

  const popoverPlacement = isTopBarLink ? "bottom" : "top";

  const popoverContent = (
    <div>
      <StyledText className="bodyTextRegular mb-0">
        You do not have access to this page
      </StyledText>
    </div>
  );

  if (locked) {
    return (
      <Popover content={popoverContent} placement={popoverPlacement}>
        <button css={lockedNavLinkStyle}>
          <div css={topbarTitleContainerStyle}>
            {icon && <div css={iconWrapperStyle}>{icon}</div>}
            <StyledText className=" bodyTextRegular mb-0">
              {children}
            </StyledText>
          </div>
          <FontAwesomeIcon color={colors.textSecondary} icon={solid("lock")} />
        </button>
      </Popover>
    );
  }

  return (
    <NavLink
      to={to}
      onClick={onClick}
      end={exact || false}
      css={navLinkStyle}
      style={({ isActive }) => {
        if (!isActive) {
          return {};
        }
        return {
          backgroundColor: colors.ctaLight,
          color: colors.ctaMain,
        };
      }}
      onMouseEnter={() => {
        setShowArrow(true);
      }}
      onMouseLeave={() => {
        setShowArrow(false);
      }}
    >
      {({ isActive }) => (
        <>
          <div css={topbarTitleContainerStyle}>
            {icon && <div css={iconWrapperStyle}>{icon}</div>}
            <StyledText
              className={`${
                isActive ? "bodyTextSemibold" : "bodyTextRegular"
              } mb-0`}
            >
              {children}
            </StyledText>
          </div>
          {!isTopBarLink && (
            <div css={arrowWrapperStyle}>
              {showArrow && <FontAwesomeIcon icon={solid("arrow-right")} />}
            </div>
          )}
        </>
      )}
    </NavLink>
  );
};
